import { useEffect, useRef, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useWalletStore } from '@/stores/useWalletStore'
import { useMiningStore } from '@/stores/useMiningStore'
import { useEosTransaction } from '@/hooks/useEosTransaction'
import { getCoinIcon } from '@/lib/common'
import { CommonFooter } from '@/components/layout'
import { toFixed } from '@/lib/decimal'
import noDataImg from '@/assets/images/noData.svg'

export function LiquidityMiningPage() {
  const { t } = useTranslation()
  const { isLogin, session } = useWalletStore()
  const { liquidityFarms, fetchLiquidityFarms } = useMiningStore()
  const { transact, loading: txLoading } = useEosTransaction()
  const [menuActive, setMenuActive] = useState(0) // 0=live, 1=finished
  const [stakedOnly, setStakedOnly] = useState(false)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const accountName = session?.account?.name

  useEffect(() => {
    fetchLiquidityFarms(accountName)
    timerRef.current = setInterval(() => fetchLiquidityFarms(accountName), 10000)
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [accountName])

  const filteredFarms = useMemo(() => {
    let list = liquidityFarms.filter((f) => f.status === menuActive)
    if (stakedOnly) list = list.filter((f) => f.isStake)
    return list
  }, [liquidityFarms, menuActive, stakedOnly])

  // Stats
  const totalTvl = useMemo(() => {
    return liquidityFarms.reduce((sum, f) => sum + (f.total_balance_usdt || 0), 0)
  }, [liquidityFarms])

  const totalReward = useMemo(() => {
    return liquidityFarms.reduce((sum, f) => sum + (f.reward || 0), 0)
  }, [liquidityFarms])

  const totalEarned = useMemo(() => {
    return liquidityFarms.reduce((sum, f) => sum + (f.claimed || 0) + (f.locked || 0) + (f.released || 0), 0)
  }, [liquidityFarms])

  const handleClaim = async (farmId: number) => {
    if (!session) return
    const account = session.account
    const actions = [{
      account: 'pools.ttr',
      name: 'claim',
      authorization: [{ actor: account.name, permission: account.authority || 'active' }],
      data: { owner: account.name, pool_id: farmId },
    }]
    const result = await transact(actions, t('swap.success'))
    if (result) fetchLiquidityFarms(accountName)
  }

  return (
    <div className="w-full flex justify-center px-4 md:px-[32px] md:mt-[32px]">
      <div className="w-full max-w-[600px]">
        {/* Stats board */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-[8px] mb-[16px]">
          {[
            { label: t('mining.totalReward'), value: totalReward > 0 ? toFixed(totalReward / 1e6, 4) + ' TTR' : '--' },
            { label: t('mining.tvl'), value: totalTvl > 0 ? '$' + toFixed(totalTvl, 2) : '--' },
            { label: t('mining.dailyEmission'), value: '172,800 TTR' },
            { label: t('mining.earned'), value: totalEarned > 0 ? toFixed(totalEarned / 1e6, 4) + ' TTR' : '--' },
          ].map((item) => (
            <div key={item.label} className="bg-white rounded-[16px] shadow-[0_0_8px_1px_rgba(61,98,50,0.14)] p-[12px]">
              <p className="text-[12px] text-[#999] mb-[4px]">{item.label}</p>
              <p className="text-[14px] font-bold text-foreground">{item.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center justify-between mb-[16px]">
          <div className="flex gap-[8px]">
            <button
              onClick={() => setMenuActive(0)}
              className={`px-[16px] py-[6px] rounded-full text-[13px] font-semibold cursor-pointer ${menuActive === 0 ? 'bg-[#498846] text-white' : 'bg-white text-[#666] border border-[#e5e5e5]'}`}
            >
              {t('mining.live')}
            </button>
            <button
              onClick={() => setMenuActive(1)}
              className={`px-[16px] py-[6px] rounded-full text-[13px] font-semibold cursor-pointer ${menuActive === 1 ? 'bg-[#498846] text-white' : 'bg-white text-[#666] border border-[#e5e5e5]'}`}
            >
              {t('mining.finished')}
            </button>
          </div>
          {isLogin && (
            <label className="flex items-center gap-[6px] cursor-pointer">
              <input type="checkbox" checked={stakedOnly} onChange={(e) => setStakedOnly(e.target.checked)} className="accent-[#498846]" />
              <span className="text-[12px] text-[#666]">{t('mining.stakedOnly')}</span>
            </label>
          )}
        </div>

        {/* Farm list */}
        {filteredFarms.length === 0 ? (
          <div className="bg-white rounded-[26px] shadow-[0_0_8px_1px_rgba(61,98,50,0.14)] px-[16px] py-[24px]">
            <div className="flex flex-col items-center py-[30px]">
              <img src={noDataImg} className="w-[120px] mb-[12px]" alt="" />
              <span className="text-[14px] text-[#999]">{t('public.noData')}</span>
            </div>
          </div>
        ) : (
          <div className="space-y-[12px]">
            {filteredFarms.map((farm) => (
              <div key={farm.id} className="bg-white rounded-[26px] shadow-[0_0_8px_1px_rgba(61,98,50,0.14)] px-[16px] py-[16px]">
                {/* Header */}
                <div className="flex items-center justify-between mb-[12px]">
                  <div className="flex items-center gap-[8px]">
                    {farm.token0 && farm.token1 && (
                      <div className="flex -space-x-2">
                        <img src={getCoinIcon(farm.token0.contract, farm.token0.coin)} className="w-[28px] h-[28px] rounded-full bg-gray-100 border-2 border-white" alt="" />
                        <img src={getCoinIcon(farm.token1.contract, farm.token1.coin)} className="w-[28px] h-[28px] rounded-full bg-gray-100 border-2 border-white" alt="" />
                      </div>
                    )}
                    <span className="text-[14px] font-semibold">
                      {farm.token0?.coin || '?'}/{farm.token1?.coin || '?'}
                    </span>
                  </div>
                  {farm.weight > 0 && (
                    <span className="text-[12px] text-[#498846] bg-[#e8f5e9] px-[8px] py-[2px] rounded-full">
                      {farm.weight}x
                    </span>
                  )}
                </div>

                {/* Details */}
                <div className="space-y-[6px] text-[13px]">
                  <div className="flex justify-between">
                    <span className="text-[#666]">APR</span>
                    <span className="text-[#498846] font-semibold">
                      {farm.apr > 0 ? toFixed(farm.apr * 100, 2) + '%' : '--'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#666]">{t('mining.tvl')}</span>
                    <span className="text-foreground">
                      {farm.total_balance_usdt > 0 ? '$' + toFixed(farm.total_balance_usdt, 2) : '--'}
                    </span>
                  </div>
                  {farm.isStake > 0 && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-[#666]">{t('mining.earned')}</span>
                        <span className="text-foreground">{toFixed(farm.reward / 1e6, 6)} TTR</span>
                      </div>
                      <div className="mt-[8px]">
                        <button
                          onClick={() => handleClaim(farm.id)}
                          disabled={txLoading || farm.reward <= 0}
                          className="w-full h-[36px] rounded-[18px] bg-[#498846] text-white text-[14px] font-semibold cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {t('mining.claim')}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <CommonFooter />
      </div>
    </div>
  )
}
